import { useEffect, useState } from 'react';
import adminService from '@services/admin/inventoryAttributeDefinitionService';
import operatorService from '@services/operator/inventoryAttributeDefinitionService';
import type { OrderRole } from '@components/shared/orders/types';

export type InventoryAttributeDefinition = {
  id?: string;
  name: string;
  label?: string;
  data_type?: string;
  [key: string]: unknown;
};

/**
 * Fetch the merchant's inventory attribute definitions, switching service
 * based on the caller's role. Resets to `[]` whenever `merchantId` is
 * falsy so the form doesn't keep showing definitions from a previous merchant.
 */
export function useInventoryAttributeDefinitions(
  role: OrderRole,
  merchantId: string | null | undefined,
): InventoryAttributeDefinition[] {
  const [definitions, setDefinitions] = useState<InventoryAttributeDefinition[]>([]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        if (!merchantId) {
          if (!cancelled) setDefinitions([]);
          return;
        }
        const service = role === 'admin' ? adminService : operatorService;
        const result = await service.fetchList({ merchant_id: merchantId });
        if (cancelled) return;
        if (result?.success || result?.data) {
          setDefinitions((result.data as InventoryAttributeDefinition[]) || []);
        } else {
          setDefinitions([]);
        }
      } catch (error) {
        console.error('Failed to fetch inventory attribute definitions', error);
        if (!cancelled) setDefinitions([]);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [role, merchantId]);

  return definitions;
}
